import { fromJS } from "immutable";
import { STATE } from "../../constants";
import {
  getDefaultDateTimeObj,
  getGBOTimeStamp
} from "../../utils/app/dateTime";
import driver from "./driver";
import location from "./location";
import payment from "./payment";
import followUp from "./followUp/";
import { getDriverConfirmState } from "./driver/confirm";
import pickupDate, { getPickupTimeState } from "./pickupDate";
import { getPickupLocationState, pickupLocationState } from "./pickupLocation";
import { getSelectCarClassState, selectCarClassState } from "./selectCarClass";
import { getLocationSubmitPickupDeliveryAddressState } from "./location/submitPickupDeliveryAddress";
import { getSearchByDriverLicenseState } from "./driver/searchByDriverLicense";
import extras from "./extras";

const { REPLACEMENT } = STATE.UI;

/**
 * @private
 * @name setUiReplacementState
 * @param {Object} [opts] {exampleEndpointState:Object}
 * @desc A setter for all replacement payload data
 * @returns {Function}
 */
function setUiReplacementState(opts = {}) {
  return (dispatch) => {
    if (opts.searchByDriverLicenseState) {
      dispatch(
        driver.searchByDriverLicenseState.set(
          null,
          opts.searchByDriverLicenseState
        )
      );
    }
    if (opts.driverConfirmState) {
      dispatch(driver.confirmState.set(null, opts.driverConfirmState));
    }
    if (opts.pickupLocationState) {
      dispatch(pickupLocationState.set(null, opts.pickupLocationState));
    }
    if (opts.followUpState) {
      dispatch(followUp.state.set(null, opts.followUpState));
    }
    if (opts.locationSubmitPickupDeliveryAddressState) {
      dispatch(
        location.locationSubmitPickupDeliveryAddressState.set(
          null,
          opts.locationSubmitPickupDeliveryAddressState
        )
      );
    }
    if (opts.pickupDatePickupTimeState) {
      dispatch(
        pickupDate.pickupTimeState.set("timestamp", {
          timestamp: opts.pickupDatePickupTimeState
        })
      );
    } else {
      dispatch(
        pickupDate.pickupTimeState.set("timestamp", {
          timestamp: getGBOTimeStamp(getDefaultDateTimeObj())
        })
      );
    }
    if (opts.selectCarClassState) {
      dispatch(
        selectCarClassState.set(
          REPLACEMENT.SELECT_CAR_CLASS.CHARGED_CAR_CLASS,
          opts.selectCarClassState
        )
      );
    }
    if (opts.extrasState) {
      dispatch(extras.state.set(null, opts.extrasState));
    }
    if (opts.paymentConfirmState) {
      dispatch(payment.paymentConfirmState.set(null, opts.paymentConfirmState));
    }
  };
}

/**
 * @name hydrateUiReplacementState
 * @desc hydrates gmi.ui source of truth state from session
 * @param {{actionSession: any }} [opts] Configuration options for action
 * @returns {Function}
 * @memberof actions.replacement
 * @example
 * dispatch(actions.replacement.hydrateUiReplacementState());
 * // will populate...
 * // gmi.ui.replacement.driver.searchByDriverLicense
 * // gmi.ui.replacement.driver.confirm
 * // gmi.ui.replacement.pickup_location
 * // gmi.ui.replacement.follow_up
 * // gmi.ui.replacement.pickupDate.pickup_time
 * // gmi.ui.replacement.select_car_class
 */
function hydrateUiReplacementState(opts = {}) {
  const { actionSession } = opts;

  return (dispatch, getState) => {
    const state = getState();
    const session = fromJS(actionSession) || state.getIn(STATE.SESSION);

    const searchByDriverLicenseState = getSearchByDriverLicenseState(session);
    const driverConfirmState = getDriverConfirmState(session);
    const pickupLocationState = getPickupLocationState(session);
    const followUpState = followUp.getFollowUpState(session);
    const locationSubmitPickupDeliveryAddressState =
      getLocationSubmitPickupDeliveryAddressState(session);
    const pickupDatePickupTimeState = getPickupTimeState(session);
    const selectCarClassState = getSelectCarClassState(session);
    const extrasState = extras.getExtrasState(session);
    const paymentConfirmState = payment.getPaymentConfirmState(session);

    dispatch(
      setUiReplacementState({
        searchByDriverLicenseState,
        driverConfirmState,
        extrasState,
        followUpState,
        locationSubmitPickupDeliveryAddressState,
        pickupLocationState,
        pickupDatePickupTimeState,
        selectCarClassState,
        paymentConfirmState
      })
    );
  };
}

// the following is imported in actions/index.js as "replacement"
export default {
  hydrateUiReplacementState,
  /**
   * @namespace "actions.replacement.selectCarClassState"
   */
  selectCarClassState,
  /**
   * @namespace "actions.replacement.driver"
   */
  driver,
  /**
   * @namespace "actions.replacement.location"
   */
  location,
  /**
   * @namespace "actions.replacement.payment"
   */
  payment,
  /**
   * @namespace "actions.replacement.pickupDate"
   */
  pickupDate,
  /**
   * @namespace "actions.replacement.extras"
   */
  extras,
  /**
   * @namespace "actions.replacement.followUp"
   */
  followUp
};
